// src/components/MemberProfile.js
// Members can update their photo, bio, location and preferences
import { useState, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { compressImage } from '../lib/imageUtils'
import './MemberProfile.css'

const INTERESTS = [
  'Travel', 'Fitness', 'Fashion', 'Photography', 'Music',
  'Food & Dining', 'Art', 'Sports', 'Gaming', 'Business',
  'Film', 'Outdoors', 'Luxury', 'Cars', 'Tech'
]

export default function MemberProfile({ onSaved }) {
  const { session, profile, refreshProfile } = useAuth()
  const userId = session?.user?.id
  const [form, setForm] = useState({
    display_name: profile?.display_name || '',
    bio:          profile?.bio          || '',
    location:     profile?.location     || '',
    preferences:  profile?.preferences  || {}
  })
  const [avatarPreview, setAvatarPreview] = useState(profile?.avatar_url || null)
  const [avatarFile, setAvatarFile]       = useState(null)
  const [saving, setSaving]   = useState(false)
  const [saved,  setSaved]    = useState(false)
  const fileRef = useRef(null)

  function toggleInterest(interest) {
    setForm(f => {
      const current = f.preferences?.interests || []
      const updated = current.includes(interest)
        ? current.filter(i => i !== interest)
        : [...current, interest]
      return { ...f, preferences: { ...f.preferences, interests: updated } }
    })
  }

  async function handleAvatarSelect(e) {
    const file = e.target.files[0]
    if (!file || !file.type.startsWith('image/')) return
    e.target.value = ''
    // Compress to 200KB for avatars
    const compressed = await compressImage(file, 200).catch(() => file)
    setAvatarFile(compressed)
    const reader = new FileReader()
    reader.onload = e => setAvatarPreview(e.target.result)
    reader.readAsDataURL(compressed)
  }

  async function handleSave(e) {
    e.preventDefault()
    if (!userId) return
    setSaving(true)

    let avatarUrl = profile?.avatar_url || null

    // Upload new avatar if selected
    if (avatarFile) {
      const path = `${userId}/avatar.jpg`
      const { error: upErr } = await supabase.storage
        .from('avatars')
        .upload(path, avatarFile, {
          contentType: 'image/jpeg',
          upsert: true
        })
      if (!upErr) {
        const { data: { publicUrl } } = supabase.storage
          .from('avatars').getPublicUrl(path)
        avatarUrl = `${publicUrl}?t=${Date.now()}`
      }
    }

    // Update profile
    const { error } = await supabase
      .from('profiles')
      .update({
        display_name:       form.display_name.trim() || profile?.display_name,
        bio:                form.bio.trim() || null,
        location:           form.location.trim() || null,
        preferences:        form.preferences,
        avatar_url:         avatarUrl,
        profile_updated_at: new Date().toISOString()
      })
      .eq('id', userId)

    setSaving(false)
    if (error) { alert('Failed to save profile. Try again.'); return }

    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    if (refreshProfile) refreshProfile()
    if (onSaved) onSaved()
  }

  const interests = form.preferences?.interests || []
  const tier = profile?.tier || 'gold'

  return (
    <div className="member-profile">
      <div className="member-profile__header">
        <p className="member-profile__eyebrow">Your Profile</p>
        <h2 className="member-profile__title">Personal Details</h2>
        <p className="member-profile__sub">
          Your profile is private — only Sky Bri can see your details.
        </p>
      </div>

      <form onSubmit={handleSave}>

        {/* AVATAR */}
        <div className="member-profile__avatar-section">
          <div
            className="member-profile__avatar"
            onClick={() => fileRef.current?.click()}
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="Your avatar" className="member-profile__avatar-img" />
            ) : (
              <div className="member-profile__avatar-placeholder">
                <span>{profile?.display_name?.[0]?.toUpperCase() || '?'}</span>
              </div>
            )}
            <div className="member-profile__avatar-overlay">
              <span>📷 Change</span>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarSelect}
            style={{ display: 'none' }}
          />
          <div className="member-profile__avatar-info">
            <p className="member-profile__avatar-name">{profile?.display_name}</p>
            <span className={`badge badge--${tier}`}>
              {tier.charAt(0).toUpperCase() + tier.slice(1)} Member
            </span>
            <p className="member-profile__avatar-hint">
              Tap your photo to change it
            </p>
          </div>
        </div>

        {/* NAME */}
        <div className="field">
          <label>Display Name</label>
          <input
            type="text"
            placeholder="How Sky Bri addresses you"
            value={form.display_name}
            onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
            maxLength={40}
          />
        </div>

        {/* BIO */}
        <div className="field">
          <label>
            About You <span style={{ color: 'var(--dim)', fontSize: 11 }}>(optional)</span>
          </label>
          <textarea
            placeholder="Tell Sky Bri a little about yourself..."
            value={form.bio}
            onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
            rows={3}
            maxLength={300}
            style={{
              width: '100%',
              background: 'var(--bg-input)',
              border: '0.5px solid var(--border)',
              color: 'var(--cream)',
              padding: '12px 16px',
              fontSize: 16,
              fontFamily: "'Jost', sans-serif",
              outline: 'none',
              resize: 'none',
              lineHeight: 1.6,
              WebkitAppearance: 'none',
              borderRadius: 0
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--dim)' }}>{form.bio.length}/300</span>
        </div>

        {/* LOCATION */}
        <div className="field">
          <label>
            Location <span style={{ color: 'var(--dim)', fontSize: 11 }}>(optional)</span>
          </label>
          <input
            type="text"
            placeholder="e.g. New York, USA"
            value={form.location}
            onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
            maxLength={60}
          />
        </div>

        {/* INTERESTS */}
        <div className="field">
          <label>Your Interests <span style={{ color: 'var(--dim)', fontSize: 11 }}>(select all that apply)</span></label>
          <div className="member-profile__interests">
            {INTERESTS.map(interest => (
              <button
                key={interest}
                type="button"
                className={`member-profile__interest-btn ${interests.includes(interest) ? 'member-profile__interest-btn--active' : ''}`}
                onClick={() => toggleInterest(interest)}
              >
                {interest}
              </button>
            ))}
          </div>
        </div>

        <button
          className="btn btn--gold btn--full"
          type="submit"
          disabled={saving}
          style={{ marginTop: 8 }}
        >
          {saving ? 'Saving...' : saved ? '✓ Profile Saved' : 'Save Profile'}
        </button>

      </form>
    </div>
  )
}
